export enum HostContactFormLocation {
  AfterEstimate = 'after_estimate',
  BeforeEstimate = 'before_estimate',
}

export enum PricingDisplay {
  Range = 'range',
  Exact = 'exact',
  Savings = 'savings',
}

// Experience knobs from authorize. Branding / origins stay elsewhere — not in this object.
// Forwarded on x-embed-customizations (JSON).
export type EmbedCustomizations = {
  hostContactFormLocation: HostContactFormLocation;
  pricingDisplay: PricingDisplay;
  showReviews: boolean;
};

const defaultEmbedCustomizations: EmbedCustomizations = {
  hostContactFormLocation: HostContactFormLocation.AfterEstimate,
  pricingDisplay: PricingDisplay.Range,
  showReviews: true,
};

// Normalize platform / header strings — anything other than before_estimate is after_estimate.
export const parseHostContactFormLocation = (value: string | null | undefined): HostContactFormLocation =>
  value === HostContactFormLocation.BeforeEstimate
    ? HostContactFormLocation.BeforeEstimate
    : HostContactFormLocation.AfterEstimate;

// Anything other than exact/savings is range — matches the backend default (CONS-2904).
export const parsePricingDisplay = (value: string | null | undefined): PricingDisplay => {
  if (value === PricingDisplay.Exact) return PricingDisplay.Exact;
  if (value === PricingDisplay.Savings) return PricingDisplay.Savings;
  return PricingDisplay.Range;
};

export const parseShowReviews = (value: unknown): boolean => value !== false;

export const serializeEmbedCustomizations = (customizations: EmbedCustomizations): string =>
  JSON.stringify(customizations);

export const parseEmbedCustomizations = (raw: string | null): EmbedCustomizations => {
  if (!raw) {
    return defaultEmbedCustomizations;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return defaultEmbedCustomizations;
    }
    const { hostContactFormLocation, pricingDisplay, showReviews } = parsed as {
      hostContactFormLocation?: string;
      pricingDisplay?: string;
      showReviews?: unknown;
    };
    return {
      hostContactFormLocation: parseHostContactFormLocation(hostContactFormLocation),
      pricingDisplay: parsePricingDisplay(pricingDisplay),
      showReviews: parseShowReviews(showReviews),
    };
  } catch {
    return defaultEmbedCustomizations;
  }
};
